import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { normalizeEmail } from 'utils/emails';

/**
 * Returns true if the given email (normalized) appears in threadTo or threadCc.
 */
export function recipientsContainPrimary(
  threadTo: string[],
  threadCc: string[],
  email: string
): boolean {
  const normalized = normalizeEmail(email);
  const allRecipients = [...(threadTo ?? []), ...(threadCc ?? [])];
  return allRecipients.some((r) => normalizeEmail(r) === normalized);
}

/**
 * Default From address in EmailComposer From input: primary in recipients → primary; else shared in recipients → that;
 * else inbox.
 * Prefers To over CC when multiple of user's send-as addresses appear in recipients.
 */
export function getDefaultFromAddress(
  threadTo: string[],
  threadCc: string[],
  inboxEmailAddress: string,
  sendAsAddresses: string[]
): string {
  const toList = threadTo ?? [];
  const ccList = threadCc ?? [];
  const inbox = (inboxEmailAddress ?? '').trim();
  const sendAs = sendAsAddresses ?? [];

  if (recipientsContainPrimary(toList, ccList, inbox)) {
    return inbox || '';
  }

  const normalizedSendAs = new Set(sendAs.map(normalizeEmail));
  for (const addr of toList) {
    if (normalizedSendAs.has(normalizeEmail(addr))) {
      const match = sendAs.find(
        (s) => normalizeEmail(s) === normalizeEmail(addr)
      );
      return match ?? addr;
    }
  }
  for (const addr of ccList) {
    if (normalizedSendAs.has(normalizeEmail(addr))) {
      const match = sendAs.find(
        (s) => normalizeEmail(s) === normalizeEmail(addr)
      );
      return match ?? addr;
    }
  }

  return inbox || '';
}

/**
 * Default To recipients for Reply mode: thread participants minus the user's
 * inbox and send-as addresses (so we don't suggest the user's own addresses).
 * Uses normalized comparison. Safe to call with sendAsAddresses empty (e.g. before API completes).
 */
export function getDefaultReplyRecipients(
  threadParticipants: string[],
  inboxEmailAddress: string,
  sendAsAddresses: string[]
): string[] {
  const inboxNorm = normalizeEmail(inboxEmailAddress ?? '');
  const sendAsSet = new Set(
    (sendAsAddresses ?? []).map((addr) => normalizeEmail(addr))
  );
  const seen = new Set<string>();
  return (threadParticipants ?? []).filter((email) => {
    if (!email) return false;
    const n = normalizeEmail(email);
    if (n === inboxNorm || sendAsSet.has(n)) return false;
    if (seen.has(n)) return false;
    seen.add(n);
    return true;
  });
}

/**
 * Draft preview To for Reply All: thread From + To, minus self.
 * Thread Cc must not be folded in here — those belong on Cc so Reply All
 * still shows and sends them when applied recipients replace Graph/Office Cc.
 */
export function getDefaultDraftToRecipients(
  threadFrom: MaybeUndef<string>,
  threadTo: Undef<string[]>,
  inboxEmailAddress: string,
  sendAsAddresses: string[]
): string[] {
  return getDefaultReplyRecipients(
    [...(threadFrom ? [threadFrom] : []), ...(threadTo ?? [])],
    inboxEmailAddress,
    sendAsAddresses
  );
}

/**
 * Default draft Cc: thread Cc (minus self) plus Quick Quote config Cc.
 * Shared by Reply and Reply All preview/apply (single editable Cc field).
 */
export function getDefaultDraftCcRecipients(
  threadCc: Undef<string[]>,
  configCc: Undef<string[]>,
  inboxEmailAddress: string,
  sendAsAddresses: string[]
): string[] {
  return getDefaultReplyRecipients(
    [...(threadCc ?? []), ...(configCc ?? [])],
    inboxEmailAddress,
    sendAsAddresses
  );
}
